/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { Fragment } from 'react';
import TransitionGroup from 'react-addons-css-transition-group';
import { isEmpty, map } from 'lodash';
import cx from 'classnames';

import {
  clearSelection,
  selectItemById,
  nextPage,
  prevPage
} from '@broadleaf/admin-components/dist/form/item-browser/actions/itemBrowser';
import ItemBrowserThumbnailTile from '../ItemBrowserThumbnailTile';
import TileGrid from '@broadleaf/admin-components/dist/form/item-browser/components/TileGrid';
import useConnectItemBrowser from '@broadleaf/admin-components/dist/form/item-browser/hooks/useConnectItemBrowser';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import UploadPreviewBar from '@broadleaf/admin-components/dist/common/components/UploadPreviewBar';
import ListGrid from '@broadleaf/admin-components/dist/collection/components/ListGrid';
import { SelectableType } from '@broadleaf/admin-components/dist/common/constants';
import ColumnRenderer from '@broadleaf/admin-components/dist/form/renderers/ColumnTypeRenderer';

import ItemBrowserGridEmptyDisplay from '@src/components/form/item-browser/ItemBrowserGrid/components/ItemBrowserGridEmptyDisplay/index';
import ItemBrowserGridLoading from '@src/components/form/item-browser/ItemBrowserGrid/components/ItemBrowserGridLoading';
import messages from './ItemBrowserGrid.messages';
import type { ICommonGridHeader } from '@broadleaf/admin-components/dist/types/common';

/**
 * A component the connects to the ItemBrowser state, and renders a TileGrid or
 * ListGrid of the items. It also renders a dropzone and UploadProgressBar, to
 * help provide uploads. This passes through the props requried for either
 * an ItemBrowserListGrid or ItemBrowserTileGrid.
 */
export const ItemBrowserGrid: React.FC<ItemBrowserGridProps> = props => {
  const { actions, dropzone, state } = useConnectItemBrowser({
    clearSelection,
    selectItemById,
    nextPage,
    prevPage
  });

  function onTileClick(item) {
    item[props.idKey] === state.itemsUI.selectedId
      ? actions.clearSelection()
      : actions.selectItemById(item[props.idKey]);
  }

  function onRowClick(checked, row) {
    checked
      ? actions.selectItemById(row[props.idKey])
      : actions.clearSelection();
  }

  function onPageChange(forward) {
    forward ? actions.nextPage() : actions.prevPage();
  }

  const page = state.itemsAPI.pages[state.itemsAPI.paging.pageKey];
  const items = page ? page.result.map(id => state.entities.items[id]) : [];
  const paginateProps = {
    onPageChange,
    pageFirst: page.first,
    pageLast: page.last
  };
  return (
    <div
      {...dropzone.getRootProps({
        className: cx(
          'ItemBrowserGrid tw-relative tw-flex-auto tw-overflow-x-hidden tw-overflow-y-auto tw-duration-300 tw-ease-in-out tw-transition-all tw-outline-none tw-min-h-[calc(100%-60px)] motion-reduce:transition-none ',
          {
            'ItemBrowserGrid--with-uploads tw-pb-20': !isEmpty(
              dropzone.state.uploadIds
            )
          }
        )
      })}
    >
      {props.enableUploads && <input {...dropzone.getInputProps()} />}

      {isEmpty(items) && page.isFetching && <ItemBrowserGridLoading />}
      {isEmpty(items) && !page.isFetching && (
        <ItemBrowserGridEmptyDisplay message={props.emptyMessage} />
      )}

      {!isEmpty(items) && state.itemsUI.displayType === 'TILE' && (
        <ItemBrowserTileGrid
          actions={actions}
          idKey={props.idKey}
          itemIds={page.result}
          paginateProps={paginateProps}
          tileComponent={props.tileComponent}
          tileProps={{
            altKey: props.altKey,
            embedCodeKey: props.embedCodeKey,
            onClick: onTileClick,
            primaryKey: props.primaryKey,
            srcKey: props.srcKey,
            tagsKey: props.tagsKey,
            titleKey: props.titleKey,
            typeKey: props.typeKey,
            ...props.tileProps
          }}
        />
      )}

      {!isEmpty(items) && state.itemsUI.displayType === 'LIST' && (
        <ItemBrowserListGrid
          idKey={props.idKey}
          onSelectRow={onRowClick}
          paginateProps={paginateProps}
          rows={items}
          rowComponent={props.rowComponent}
          rowHeaderComponent={props.rowHeaderComponent}
          rowProps={props.rowProps}
          selectedIds={[state.itemsUI.selectedId]}
          srcKey={props.srcKey}
          tagsKey={props.tagsKey}
          titleKey={props.titleKey}
          typeKey={props.typeKey}
        />
      )}
      <TransitionUploadPreviewBar
        onClose={dropzone.clearUploads}
        uploads={map(
          dropzone.state.uploadIds,
          id => dropzone.state.uploadsById[id]
        )}
      />
    </div>
  );
};

ItemBrowserGrid.defaultProps = {
  altKey: 'alt',
  embedCodeKey: 'embedCode',
  emptyMessage: 'Drop files here to upload',
  enableUploads: true,
  idKey: 'id',
  mimeTypeKey: 'mimeType',
  primaryKey: 'primary',
  srcKey: 'url',
  tagsKey: 'tags',
  titleKey: 'title',
  typeKey: 'type'
};

export interface ItemBrowserGridProps {
  /** The property key for getting an item's alt value */
  altKey: string;
  /** The property key for getting an item's embed code value. This is used for item's without a source url and instead contain embedded content.  */
  embedCodeKey: string;
  /** The message that is rendered when the grid is empty */
  emptyMessage: string;
  /** Enables an upload dropzone to be rendered for the ItemBrowserBody */
  enableUploads: boolean;
  /** The property key for getting an item's id value */
  idKey: string;
  /** The property key for getting an item's mimeType value */
  mimeTypeKey: string;
  /** The property key for getting an item's primary value, if it exists */
  primaryKey: string;
  /**
   * The component used for rendering the row. Typically this will default to
   * a 'tr' HTML element if not provided.
   */
  rowComponent: React.ReactNode | string;

  /**
   * The component used for rendering the row header. Typically this will default
   * to a 'tr' HTML element if not provided.
   */
  rowHeaderComponent: React.ReactNode | string;

  /**
   * Any additional props to pass down to each row component. This is useful for
   * passing down shared props that may not be resident on each row's data.
   */
  rowProps: object;
  /** The property key for getting an item's src value */
  srcKey: string;
  /** The property key for getting an item's tags value */
  tagsKey: string;

  /**
   * The component used for rendering the tile. Typically this will default to
   * a `ItemBrowserThumbnailTile` component if not provided.
   */
  tileComponent: React.ReactNode | string;

  /**
   * Any additional props to pass down to each tile component. This is useful for
   * passing down shared props that may not be resident on each item's data.
   */
  tileProps: object;

  /** The property key for getting an item's title value */
  titleKey: string;
  /** The property key for getting an item's type value */
  typeKey: string;
}

const ItemBrowserTileGrid = props => {
  return (
    <TileGrid
      {...props}
      tileComponent={
        props.tileComponent ? props.tileComponent : ItemBrowserThumbnailTile
      }
    />
  );
};

export const ItemBrowserListGrid: React.FC<ItemBrowserListGridProps> = props => {
  const formatMessage = useFormatMessage();
  const headers: Array<ICommonGridHeader> = [
    {
      classifier: 'Field',
      name: props.srcKey,
      label: formatMessage(messages.thumbnail),
      type: 'THUMBNAIL',
      attributes: {
        typeKey: props.typeKey
      }
    },
    {
      classifier: 'Field',
      name: props.titleKey,
      label: formatMessage(messages.title),
      type: 'STRING',
      attributes: {
        colSpan: 2
      }
    },
    {
      classifier: 'Field',
      name: props.typeKey,
      label: formatMessage(messages.type),
      type: 'STRING'
    },
    {
      classifier: 'Field',
      name: props.tagsKey,
      label: formatMessage(messages.tags),
      type: 'TAGS_ARRAY'
    }
  ];

  function renderColumn(columnData) {
    return <ColumnRenderer {...columnData} />;
  }

  return (
    <ListGrid
      headers={headers}
      onSelectRow={props.onSelectRow}
      paginateProps={props.paginateProps}
      renderColumn={renderColumn}
      rows={props.rows}
      rowKey={props.idKey}
      rowComponent={props.rowComponent}
      rowHeaderComponent={props.rowHeaderComponent}
      rowProps={props.rowProps}
      selectable
      selectableType={SelectableType.is.SINGLE}
      selectedIds={props.selectedIds}
    />
  );
};

export interface ItemBrowserListGridProps {
  paginateProps: {
    disabled?: boolean;
    nextLabel?: string | React.ReactNode | Element;
    onPageChange?: Function;
    pageFirst?: boolean;
    pageLast?: boolean;
    prevLabel?: string | React.ReactNode | Element;
  };
  /** The property key for getting an item's src value */
  srcKey: string;
  /** The property key for getting an item's type value */
  typeKey: string;
  /** The property key for getting an item's title value */
  titleKey: string;
  /** The property key for getting an item's tags value */
  tagsKey: string;
  /**
   * Handler for when a row is clicked for selection.
   */
  onSelectRow: Function;
  /**
   * The set of rows to render for the ListGrid component.
   */
  rows: Array<Record<string, any>>;
  /** The property key for getting an item's ID value */
  idKey: string;
  /**
   * The component used for rendering the row. Typically this will default to
   * a 'tr' HTML element if not provided.
   */
  rowComponent?: React.ReactNode | string;
  /**
   * The component used for rendering the row header. Typically this will default
   * to a 'tr' HTML element if not provided.
   */
  rowHeaderComponent?: React.ReactNode | string;
  /**
   * Any additional props to pass down to each row component. This is useful for
   * passing down shared props that may not be resident on each row's data.
   */
  rowProps: Record<string, any>;
  /**
   * The keys of the currently selected rows.
   */
  selectedIds: Array<string>;
}

const TransitionUploadPreviewBar = props => {
  return (
    <TransitionGroup
      component={Fragment}
      transitionName="ItemBrowserBody__ItemUploadPreviewBar-"
      transitionEnterTimeout={300}
      transitionLeaveTimeout={300}
    >
      {!isEmpty(props.uploads) && (
        <UploadPreviewBar
          className="ItemBrowserBody__ItemUploadPreviewBar tw-shrink-0 tw-grow-0"
          {...props}
        />
      )}
    </TransitionGroup>
  );
};

export default ItemBrowserGrid;
